import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Gavel, Loader2, Sparkles, Scale } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../lib/dates';
import { JudgeStrategyProfile } from '../components/judicial/JudgeStrategyProfile';

const VOTE = { favor: ['A favor', 'bg-emerald-500'], contra: ['En contra', 'bg-red-500'], voto_disidente: ['Disidente', 'bg-orange-500'], voto_concurrente: ['Concurrente', 'bg-sky-500'], sin_voto: ['Sin voto registrado', 'bg-slate-500'] };
const TEND = { favorable_demandante: 'Favorable al demandante', favorable_demandado: 'Favorable al demandado', mixta: 'Mixta', sin_datos: 'Sin datos suficientes' };

const VotesByArea = ({ data }) => {
  const rows = Object.entries(data || {});
  if (!rows.length) return <p data-testid="judge-votes-empty" className="text-sm text-slate-500">Aún no hay fallos registrados en la plataforma para este juez.</p>;
  return (
    <div data-testid="judge-votes-by-area" className="space-y-3">
      {rows.map(([area, v]) => (
        <div key={area} data-testid={`judge-area-${area}`}>
          <div className="flex justify-between text-xs mb-1"><span className="capitalize text-slate-200">{area}</span><span className="text-slate-500">{v.total} fallo(s)</span></div>
          <div className="flex h-2.5 overflow-hidden rounded-full bg-slate-800">
            {Object.entries(v).filter(([k]) => k !== 'total').map(([k, n]) => (
              <div key={k} title={`${(VOTE[k] || [k])[0]}: ${n}`} className={(VOTE[k] || [k, 'bg-slate-400'])[1]} style={{ width: `${(n / v.total) * 100}%` }} />
            ))}
          </div>
          <p className="mt-1 text-[11px] text-slate-500">{Object.entries(v).filter(([k]) => k !== 'total').map(([k, n]) => `${(VOTE[k] || [k])[0]}: ${n}`).join(' · ')}</p>
        </div>
      ))}
    </div>
  );
};

const AiSummary = ({ summary, onGenerate, busy }) => (
  <div data-testid="judge-ai-summary" className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
    <div className="flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-300"><Sparkles className="h-4 w-4" />Línea de votación por materia</h2>
      <button data-testid="judge-ai-summary-btn" onClick={onGenerate} disabled={busy} className="rounded-full bg-amber-500 px-4 py-1.5 text-xs font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60 transition-colors">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : summary ? 'Actualizar resumen IA' : 'Generar resumen IA'}
      </button>
    </div>
    <p className="mt-1 text-[11px] text-amber-200/70">Estimación de la IA · no oficial · verifica con las sentencias</p>
    {summary ? (
      <>
        <p className="mt-3 text-sm text-slate-200">{summary.resumen}</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {summary.areas?.map((a) => (
            <div key={a.area} className="rounded-xl border border-slate-800 bg-slate-900/70 p-3">
              <p className="text-xs font-semibold capitalize text-white">{a.area} <span className="ml-1 font-normal text-slate-400">· {TEND[a.tendencia] || a.tendencia}</span></p>
              <p className="mt-1 text-xs text-slate-400">{a.linea}</p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[10px] text-slate-500">Basado en {summary.based_on_rulings} fallo(s) de la plataforma · {fmtDate(summary.generated_at)}</p>
      </>
    ) : <p className="mt-3 text-sm text-slate-500">Genera un resumen de cómo vota este juez en cada materia.</p>}
  </div>
);

export default function JudgePage() {
  const { judgeId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/judicial-intelligence/judges/${judgeId}/profile`).then((r) => setP(r.data)).catch(() => setP({ error: true })); }, [judgeId, api]);
  const generate = async () => {
    setBusy(true);
    try { const r = await api.post(`/judicial-intelligence/judges/${judgeId}/voting-summary`, {}, { timeout: 180000 }); setP((x) => ({ ...x, ai_summary: r.data })); }
    catch { toast.error('No se pudo generar el resumen'); } finally { setBusy(false); }
  };
  if (!p) return <div className="flex min-h-screen items-center justify-center bg-slate-950"><Loader2 className="h-6 w-6 animate-spin text-amber-400" /></div>;
  if (p.error) return <div data-testid="judge-page-error" className="min-h-screen bg-slate-950 p-10 text-slate-300">Juez no encontrado.</div>;
  const j = p.judge;
  const sumVotes = (keys) => Object.values(p.votes_by_area || {}).reduce((s, v) => s + keys.reduce((a, k) => a + (v[k] || 0), 0), 0);
  return (
    <div data-testid="judge-page" className="min-h-screen bg-slate-950 text-white">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-900/80 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-4 px-4 sm:px-6 text-sm">
          <button data-testid="judge-page-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" />Volver</button>
          <Link to="/crm" data-testid="judge-nav-crm" className="text-slate-400 hover:text-white">CRM de Casos</Link>
          <Link to="/inteligencia-judicial" data-testid="judge-nav-judicial" className="text-slate-400 hover:text-white">Inteligencia Judicial</Link>
          <Link to="/cortes" data-testid="judge-nav-courts" className="text-slate-400 hover:text-white">Cortes y fiscales</Link>
        </div>
      </header>
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="flex items-start gap-4">
          <div className="rounded-2xl bg-amber-500/15 p-3"><Gavel className="h-7 w-7 text-amber-400" /></div>
          <div>
            <h1 data-testid="judge-page-name" className="text-4xl sm:text-5xl font-bold font-['Outfit'] tracking-tight">{j.full_name || j.name}</h1>
            <p className="mt-2 text-sm text-slate-400">{j.position} · {j.court_name}{j.sala ? ` · ${j.sala}` : ''} · <span className="capitalize">{j.country}</span></p>
            {j.specialization?.length > 0 && <p className="mt-1 text-xs text-slate-500">Especialidades: {j.specialization.join(', ')}</p>}
          </div>
        </div>
        <div className="mt-8 grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-slate-900 p-4"><p data-testid="judge-page-total" className="text-2xl font-bold">{p.total_rulings}</p><p className="text-xs text-slate-400">Fallos registrados</p></div>
          <div className="rounded-xl bg-slate-900 p-4"><p data-testid="judge-page-favor" className="text-2xl font-bold">{sumVotes(['favor', 'voto_concurrente'])}</p><p className="text-xs text-slate-400">Votos a favor</p></div>
          <div className="rounded-xl bg-slate-900 p-4"><p data-testid="judge-page-dissent" className="text-2xl font-bold">{sumVotes(['voto_disidente'])}</p><p className="text-xs text-slate-400">Votos disidentes</p></div>
        </div>
        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
          <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold"><Scale className="h-4 w-4 text-sky-400" />Cómo vota por materia (fallos registrados)</h2>
            <VotesByArea data={p.votes_by_area} />
          </section>
          <AiSummary summary={p.ai_summary} onGenerate={generate} busy={busy} />
        </div>
        <JudgeStrategyProfile judgeId={judgeId} initial={j.strategy_profile} />
        <section className="mt-8">
          <h2 className="mb-3 text-sm font-semibold">Fallos recientes</h2>
          {!p.rulings?.length && <p className="text-sm text-slate-500">Sin fallos registrados. Se agregan al subir sentencias o con la búsqueda automática nocturna.</p>}
          <div className="grid gap-2" data-testid="judge-page-rulings">
            {p.rulings?.map((r) => (
              <div key={r.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
                <div className="flex justify-between gap-2"><p className="text-sm font-medium">{r.case_number}</p><span className="text-xs text-slate-400">{(VOTE[r.judge_vote] || [r.judge_vote || '—'])[0]}</span></div>
                <p className="text-xs text-slate-500">{fmtDate(r.ruling_date)} · <span className="capitalize">{r.case_type}</span> · {r.outcome}</p>
                {r.ai_summary && <p className="mt-1 text-xs text-slate-300">{r.ai_summary}</p>}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
